import classNames from "classnames";
import * as React from "react";

import styles from "./SkillFieldSection.module.css";

interface Props {
  title: string;
  /** What the field is for — the same sentence in both read and edit mode. */
  hint: string;
  children: React.ReactNode;
  /**
   * Let this section claim the page's leftover height. The procedure does; the
   * one-paragraph "when to use" doesn't, or it would stretch to fill the page.
   */
  grow?: boolean;
  /**
   * Id put on the heading, for an input inside the block to point at with
   * `aria-labelledby`. Edit mode uses this so the field's accessible name is
   * the block's own title rather than a second label repeating it.
   */
  titleId?: string;
}

/**
 * One field of a skill version, framed as a card: a header carrying the title
 * and the hint that explains what the field is for, a rule, then the content.
 *
 * Both modes render this, which is the point. Read mode used to be two bare
 * `<strong>` headings and edit mode a pair of loose MUI fields, so the same two
 * fields arrived as different-looking furniture depending on how you got here —
 * and the hints existed only while editing, leaving readers with no statement
 * that "when to use" is the text the AI matches a skill against. The shape here
 * deliberately mirrors the agent page's detail cards so the two pages read as
 * one product; the wording comes from {@link ./skillFieldCopy} so it can't
 * drift between modes again.
 */
export function SkillFieldSection({
  title,
  hint,
  children,
  grow,
  titleId,
}: Props) {
  return (
    <section
      className={classNames(styles.section, { [styles.sectionGrow]: grow })}
    >
      <div className={styles.header}>
        <h2 className={styles.title} id={titleId}>
          {title}
        </h2>
        <p className={styles.hint}>{hint}</p>
      </div>
      <div className={classNames(styles.body, { [styles.bodyGrow]: grow })}>
        {children}
      </div>
    </section>
  );
}
